/**
 * Lyrics LRC helpers — parse and emit the standard LRC format.
 * Tag rules: each timed line is `[mm:ss.xx]text` (xx = hundredths).
 * Multiple timestamps for one line ([00:12.34][00:48.99]La la la) are supported.
 * Metadata tags ([ti:], [ar:], [al:], [by:], [length:], …) are returned in
 * `metadata` but ignored when playing.
 */

const TS_RE = /\[(\d{1,2}):(\d{2}(?:\.\d{1,3})?)\]/g;
const META_RE = /^\[([a-z]+):([^\]]*)\]/i;

/** "3:42.50" -> 222.5 seconds */
export const parseLrcTimestamp = (mm, ss) => {
  const m = parseInt(mm, 10);
  const s = parseFloat(ss);
  if (Number.isNaN(m) || Number.isNaN(s)) return null;
  return m * 60 + s;
};

/** seconds -> "[mm:ss.xx]" */
export const formatLrcTimestamp = (totalSeconds) => {
  const s = Math.max(0, totalSeconds);
  const m = Math.floor(s / 60);
  const rest = s - m * 60;
  return `[${String(m).padStart(2, "0")}:${rest.toFixed(2).padStart(5, "0")}]`;
};

/**
 * Parse an LRC string into { metadata, lines: [{time, text}] }.
 * Lines without timestamps are skipped (treated as headers).
 */
export const parseLrc = (raw) => {
  const lines = [];
  const metadata = {};
  if (!raw) return { metadata, lines };
  for (const rawLine of raw.replace(/\r/g, "").split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;
    // Metadata-only?
    const metaMatch = line.match(META_RE);
    if (metaMatch && !/^\d/.test(metaMatch[1]) && metaMatch[1].length <= 8) {
      // Skip false positives (e.g. "[01:" is *not* metadata)
      if (!/^\d{1,2}:/.test(line.slice(1))) {
        metadata[metaMatch[1].toLowerCase()] = metaMatch[2];
        // Only continue if no timestamps after the meta tag
        if (!TS_RE.test(line)) {
          TS_RE.lastIndex = 0;
          continue;
        }
      }
    }
    TS_RE.lastIndex = 0;
    const timestamps = [];
    let m;
    while ((m = TS_RE.exec(line)) !== null) {
      const t = parseLrcTimestamp(m[1], m[2]);
      if (t !== null) timestamps.push(t);
    }
    if (timestamps.length === 0) continue;
    const text = line.replace(TS_RE, "").trim();
    for (const t of timestamps) {
      lines.push({ time: t, text });
    }
  }
  // Sort by time and stable
  lines.sort((a, b) => a.time - b.time);
  return { metadata, lines };
};

/**
 * Build an LRC string from an ordered list of { time, text }.
 */
export const buildLrc = (lines, metadata = {}) => {
  const out = [];
  const metaKeys = ["ti", "ar", "al", "by", "length", "offset"];
  for (const k of metaKeys) {
    if (metadata[k]) out.push(`[${k}:${metadata[k]}]`);
  }
  for (const ln of lines) {
    if (ln.time === null || ln.time === undefined) continue;
    out.push(`${formatLrcTimestamp(ln.time)}${ln.text || ""}`);
  }
  return out.join("\n");
};

/**
 * Apply a speed multiplier to LRC lines without modifying the original times.
 * A speed of 1.1 means the band plays 10% faster than at the recording, so the
 * displayed times need to be DIVIDED by 1.1 (lines come earlier).
 */
export const scaleLrcLines = (lines, speed) => {
  const s = Number(speed) > 0 ? Number(speed) : 1;
  return lines.map((ln) => ({ ...ln, time: ln.time / s }));
};

/**
 * Find the index of the active line for a given playback time. Returns -1
 * when we haven't reached the first line yet.
 */
export const activeLineIndex = (lines, currentTime) => {
  if (!lines || lines.length === 0) return -1;
  // Linear scan is fine — songs rarely have > 200 lines
  let idx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].time <= currentTime) idx = i;
    else break;
  }
  return idx;
};
